import React from 'react';
import { Stack, Box, SvgIcon } from '@mui/material';
import { useAtomValue } from 'jotai';
import { PoiIcon } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/PoiIcon';
import * as analysisState from '@/components/Analysis/state';

interface PoiIconsListProps {
  poiItems: Array<{ poiType: string; poiTemplateKey?: string | null | undefined }>;
  size?: number; // Size in pixels (default: 20)
  spacing?: number; // Spacing between icons (default: -0.5 for overlap)
}

/**
 * Reusable component that renders a list of POI icons with their proper colors.
 * Icons are displayed in a stacked/overlapping style.
 *
 * @param poiTypes - Array of POI type names
 * @param size - Size of each icon in pixels (default: 20)
 * @param spacing - Spacing between icons, negative for overlap (default: -0.5)
 */
export const PoiIconsList: React.FC<PoiIconsListProps> = ({
  poiItems,
  size = 2,
  spacing = -0.5,
}) => {
  const poiTypeToColorMap = useAtomValue(analysisState.poiTypeToColorMap);

  if (!poiItems || poiItems.length === 0) {
    return null;
  }

  return (
    <Stack direction="row" alignItems="center" spacing={spacing} sx={{ flexShrink: 0 }}>
      {poiItems.map((item, index) => {
        const poiType = item.poiType || '';
        const normalizedType = poiType.toLowerCase();
        const backgroundColor = poiTypeToColorMap.get(normalizedType) || '#FFFFFF';

        return (
          <Box
            key={`${poiType}-${index}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: `${size}rem`,
              height: `${size}rem`,
              borderRadius: '50%',
              backgroundColor: backgroundColor,
              border: `0.1rem solid ${backgroundColor}`,
              boxSizing: 'border-box',
              zIndex: poiItems.length + index,
            }}
          >
            <SvgIcon
              sx={{
                width: `calc(${size}rem - 0.6rem)`,
                height: `calc(${size}rem - 0.6rem)`,
                fontSize: `calc(${size}rem - 0.6rem)`,
                color: 'white !important', // Force white color for all icons including question mark
                '& path': {
                  fill: 'currentColor !important', // Ensure SVG paths inherit the white color
                },
                '& svg': {
                  color: 'white !important', // Nested SVG color
                },
              }}
            >
              <PoiIcon name={poiType} poiTemplateKey={item.poiTemplateKey} />
            </SvgIcon>
          </Box>
        );
      })}
    </Stack>
  );
};
